import { useMultistepForm } from '@/hooks/use-multistep-form.tsx';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button.tsx';
import { Link, useNavigate } from 'react-router';
import { useCreatePlant } from '@/features/plants/hooks/mutations/use-create-plant.ts';
import { TPlant, PlantSchema } from '@/features/plants/schemas/plants.schema.ts';
import { CreatePlantFormNavigator } from '../shared/form-navigator.tsx';
import { STEPS_CREATE } from './plant-form-create-steps.ts';
import { Suspense } from 'react';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { LoaderIcon } from 'lucide-react';

export const CreatePlantForm = () => {
  const navigate = useNavigate();

  const {
    step: StepElement,
    isFirstStep,
    isLastStep,
    currentStepIndex,
    goTo,
    back,
    next,
  } = useMultistepForm(STEPS_CREATE.map((S) => <S.Element showTitle={true} />));

  const form = useForm({
    resolver: zodResolver(PlantSchema),
    mode: 'onChange',
    defaultValues: {
      plantId: '',
      companyId: '',
      basicDetails: {
        name: '',
        type: '',
        status: 'Maintenance',
        shortDescription: '',
      },
      siteInformation: {
        country: {
          label: '',
          value: '',
        },
        state: {
          label: '',
          value: '',
        },
        city: {
          label: '',
          value: '',
        },
        coordinates: {
          latitude: '',
          longitude: '',
        },
        siteAddress: '',
        siteArea: '',
        siteAccess: {
          label: '',
          value: '',
        },
      },
      technicalSpecifications: {
        capacity: '',
      },
      equipmentAndConnectionDetails: {
        devices: [],
      },
      financialInformation: {
        tariffRate: '',
        expectedROI: '',
        operationalCosts: '',
      },
      contactDetails: {
        owner: {
          firstName: '',
          lastName: '',
        },
        ownerEmail: '',
        ownerPhoneNumber: '',
        installer: '',
        installerEmail: '',
        installerPhoneNumber: '',
      },
      dataCollectionInterval: '1min',
      createdAt: '',
      updatedAt: '',
    },
  });

  const createPlantMutation = useCreatePlant({
    onSuccess: () => {
      navigate('/plants');
    },
  });

  const onSubmit = (data: TPlant) => {
    createPlantMutation.mutate(data);
  };

  const onNext = async () => {
    if (!isLastStep) {
      const isValid = await form.trigger(STEPS_CREATE[currentStepIndex]?.schema);
      if (isValid) {
        next();
      }
    }
  };

  return (
    <div className="@container">
      <div className="grid w-full max-w-7xl grid-cols-1 items-start gap-2 @2xl:grid-cols-[1fr_250px] @2xl:gap-4">
        <div className="grid flex-1 gap-6">
          <div className="flex flex-col gap-2">
            <span className="text-muted-foreground block @2xl:hidden">
              Step {currentStepIndex + 1} from {STEPS_CREATE.length}
            </span>
            <h2 className="text-2xl font-bold">{STEPS_CREATE[currentStepIndex].title}</h2>
          </div>
          <div className="">
            <FormProvider {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="flex flex-col gap-4">
                  <Suspense
                    fallback={
                      <div className="flex h-20 items-center justify-center">
                        <LoadingSpinner size={50} />
                      </div>
                    }
                  >
                    {StepElement}
                  </Suspense>
                  <div className="flex items-center justify-end gap-2 sm:gap-4">
                    <Button variant="ghost" type="button" asChild>
                      <Link to="/plants">Cancel</Link>
                    </Button>
                    {!isFirstStep && (
                      <Button onClick={back} variant="secondary" type="button">
                        Previous
                      </Button>
                    )}
                    {!isLastStep && (
                      <Button type="button" onClick={onNext}>
                        Next
                      </Button>
                    )}
                    {isLastStep && (
                      <Button
                        disabled={createPlantMutation.isPending}
                        onClick={() => {
                          form.handleSubmit(onSubmit)();
                        }}
                        type="button"
                      >
                        {createPlantMutation.isPending && (
                          <LoaderIcon
                            className="size-4 animate-spin text-white"
                            aria-hidden="true"
                          />
                        )}
                        {createPlantMutation.isPending ? 'Submitting...' : 'Submit'}
                      </Button>
                    )}
                  </div>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
        <div className="relative h-full">
          <div className="sticky top-0">
            <CreatePlantFormNavigator
              steps={STEPS_CREATE}
              currentStepIndex={currentStepIndex}
              goTo={goTo}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
